import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AvatarModule } from 'ngx-avatars';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MessagingServiceService } from 'src/app/Services/messaging-service.service';
import { isRustAgentUser } from 'src/app/agent-deploy/agent-deploy.config';
import { sysConfig } from 'src/app/sysConfig';

@Component({
    selector: 'app-employee-search',
    imports: [
        FormsModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        CommonModule,
        AvatarModule,
        ReactiveFormsModule
    ],
    templateUrl: './employee-search.component.html',
    styleUrls: ['./employee-search.component.css']
})
export class EmployeeSearchComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() userList: any[] = [];
  @Input() placeholder: string = 'Search Employees';
  @Input() tags: string = 'team members';
  @Input() selectedEmployeeId: string | number = '';
  /** Shown when the id is set but not yet found in userList (e.g. deep link before list loads). */
  @Input() selectedEmployeeLabel: string = '';
  @Input() selectedValues: any[] = []; // For multi-select pre-selected values
  @Input() isNeedUserReturn: string | number = '';
  @Input() isShowAllEmp: Boolean = true;
  @Input() isInputSearch: Boolean = false;
  @Input() isMultiSelect: Boolean = false;
  /** Filter bars: first name + “+N” instead of wrapping every selected person. */
  @Input() compactMultiTrigger: boolean = false;
  @Input() minHeight: boolean = false;
  /** Pre-fill the search input (e.g. restored from URL query param). */
  @Input() initialValue: string = '';

  @Output() employeeSelected = new EventEmitter<string | number | any>();
  @Output() searchEmployeeName = new EventEmitter<string | number>();
  @ViewChild('searchInp') searchInput!: ElementRef;

  readonly sysConfig = sysConfig;

  employeeMultiCtrl: FormControl = new FormControl('');
  employeeSearchCtrl: FormControl = new FormControl('');
  filteredUserList: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(private messagingService: MessagingServiceService) {}

  /** Safe list: parents may bind `[userList]="null"` for search-only UI (e.g. Reports table). */
  get userListSafe(): any[] {
    return Array.isArray(this.userList) ? this.userList : [];
  }

  ngOnInit(): void {
    // Initialize filtered list
    this.filteredUserList = this.userListSafe.slice();

    // Pre-fill search input from initialValue (e.g. restored from URL)
    if (this.isInputSearch && this.initialValue) {
      this.employeeSearchCtrl.setValue(this.initialValue, { emitEvent: false });
      // Also set the native input value immediately so it renders on first paint
      setTimeout(() => {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = this.initialValue;
        }
      }, 0);
    }

    if (this.isMultiSelect) {
      this.employeeMultiCtrl.setValue(this.normalizeSelectedValues(), { emitEvent: false });
    } else {
      this.syncSelectedEmployeeFromInput();
    }

    // Subscribe to employee selection changes
    this.employeeMultiCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const selectedUserData = this.userListSafe.filter((user) =>
          this.idsMatch(user?.id, value)
        );
        if (this.isNeedUserReturn) {
          this.employeeSelected.emit(selectedUserData[0]);
        } else {
          this.employeeSelected.emit(value);
        }
      });

    // Subscribe to search input changes
    this.employeeSearchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.filterEmployees();
      });
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
      map((event: any) => event.target.value),
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.searchEmployee(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update filtered list when userList changes
    this.filteredUserList = this.userListSafe.slice();
    this.filterEmployees();

    if (this.isMultiSelect && changes['selectedValues']) {
      this.employeeMultiCtrl.setValue(this.normalizeSelectedValues(), { emitEvent: false });
    } else if (
      !this.isMultiSelect &&
      !this.isInputSearch &&
      (changes['selectedEmployeeId'] || changes['userList'] || changes['selectedEmployeeLabel'])
    ) {
      // Keep picker in sync when parent route / binding updates (e.g. Work Replay deep link).
      this.syncSelectedEmployeeFromInput();
    } else if (!this.isMultiSelect) {
      this.employeeMultiCtrl.setValue(this.selectedEmployeeId || '', { emitEvent: false });
    }
  }

  /** Align mat-select value with route-bound id (strict match for mat-option [value]). */
  private syncSelectedEmployeeFromInput(): void {
    if (this.isMultiSelect || this.isInputSearch) {
      return;
    }

    const id = this.selectedEmployeeId;
    if (id !== null && id !== undefined && id !== '') {
      const next = this.resolveEmployeeControlValue(id);
      if (this.employeeMultiCtrl.value !== next) {
        this.employeeMultiCtrl.setValue(next, { emitEvent: false });
      }
      return;
    }

    if (this.employeeMultiCtrl.value !== '' && this.employeeMultiCtrl.value != null) {
      this.employeeMultiCtrl.setValue('', { emitEvent: false });
    }
  }

  private idsMatch(a: unknown, b: unknown): boolean {
    if (a === null || a === undefined || a === '') {
      return b === null || b === undefined || b === '';
    }
    if (b === null || b === undefined || b === '') {
      return false;
    }
    return String(a) === String(b);
  }

  /** Normalize to list id type so mat-select matches mat-option values. */
  private resolveEmployeeControlValue(id: string | number | null | undefined): string | number {
    if (id === null || id === undefined || id === '') {
      return '';
    }
    const matched = this.userListSafe.find((user) => this.idsMatch(user?.id, id));
    return matched ? matched.id : id;
  }

  hasSelectedEmployeeId(): boolean {
    const v = this.employeeMultiCtrl?.value;
    if (Array.isArray(v)) {
      return v.length > 0;
    }
    return v !== null && v !== undefined && v !== '';
  }

  get compactMultiFirstUser(): any {
    const ids = this.employeeMultiCtrl?.value;
    if (!Array.isArray(ids) || ids.length === 0) {
      return null;
    }
    return this.getEmployeesNameFromId(ids[0]) || null;
  }

  get compactMultiExtraCount(): number {
    const ids = this.employeeMultiCtrl?.value;
    return Array.isArray(ids) && ids.length > 1 ? ids.length - 1 : 0;
  }

  private normalizeSelectedValues(): any[] {
    return Array.isArray(this.selectedValues)
      ? this.selectedValues.filter((id) => id !== null && id !== undefined && id !== '')
      : [];
  }

  filterEmployees(): void {
    const searchValue = this.employeeSearchCtrl.value?.toLowerCase() || '';
    const list = this.userListSafe;

    if (!searchValue) {
      this.filteredUserList = list.slice();
      return;
    }

    this.filteredUserList = list.filter((user: any) => {
      const name = user.name?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      return name.includes(searchValue) || email.includes(searchValue);
    });
  }

  getSelectedUser(): any {
    const selectedId = this.employeeMultiCtrl.value;
    if (!this.hasSelectedEmployeeId()) return null;
    return this.userListSafe.find((user: any) => this.idsMatch(user?.id, selectedId)) ?? null;
  }

  getEmployeesNameFromId(userId): any {
    return this.userListSafe.find((user: any) => this.idsMatch(user?.id, userId)) ?? '';
  }

  getSelectedDisplayName(): string {
    const user = this.getSelectedUser();
    if (user?.name) return user.name;
    if (this.hasSelectedEmployeeId() && this.selectedEmployeeLabel) {
      return this.selectedEmployeeLabel;
    }
    return '';
  }

  showRustAgentBetaBadge(userOrId?: any): boolean {
    if (!sysConfig.rustAgentBetaBadgeEnabled) {
      return false;
    }
    const user =
      userOrId && typeof userOrId === 'object'
        ? userOrId
        : userOrId != null && userOrId !== ''
          ? this.userListSafe.find((u: any) => this.idsMatch(u?.id, userOrId))
          : this.getSelectedUser();
    if (isRustAgentUser(user)) {
      return true;
    }
    const id = user?.id ?? userOrId ?? this.employeeMultiCtrl.value;
    if (id == null || id === '') {
      return false;
    }
    const fbUser = this.messagingService
      .getLatestFirebaseUsersSnapshot()
      .find((u) => this.idsMatch(u?.id, id));
    return isRustAgentUser(fbUser);
  }

  getDisplayImageUrl(user: any): string {
    return this.getProfileImageUrl(user) || this.getDefaultImageUrl();
  }

  hasProfileImage(user: any): boolean {
    return !!this.getProfileImageUrl(user);
  }

  private isValidProfileImageUrl(url: unknown): url is string {
    if (typeof url !== 'string' || url.trim() === '' || url === 'null') {
      return false;
    }
    return !/secure\.gravatar\.com/i.test(url);
  }

  getProfileImageUrl(user: any): string | null {
    if (!user) return null;

    const candidates = [
      user.meta?.profile_image_url,
      user.meta?.profile_image,
      user.profile_image_url,
      user.profile_image,
    ];

    for (const url of candidates) {
      if (this.isValidProfileImageUrl(url)) {
        return url;
      }
    }

    return null;
  }

  getDefaultImageUrl(): string {
    // Return a data URI for a simple default placeholder icon
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='%23B3B3B3'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
  }

  searchEmployee(value: any) {
    this.searchEmployeeName.emit(value)
  }

  shouldHide(user: any): boolean {
    const searchValue = this.employeeSearchCtrl.value?.toLowerCase() || '';
    if (!searchValue) return false;
    const name = user.name?.toLowerCase() || '';
    const matchesSearch = name.includes(searchValue);
    return !matchesSearch;
  }

  resetSearch() {
    this.employeeSearchCtrl.setValue('');
    if (this.isMultiSelect) {
      this.employeeMultiCtrl.setValue([], { emitEvent: false });
    } else {
      this.employeeMultiCtrl.setValue('', { emitEvent: false });
    }
    if (this.searchInput && this.searchInput.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    this.searchEmployee('');
  }

}
